import api from '../../../auth/apiClient';
import * as XLSX from 'xlsx';
import { WeighbridgeFilters, WeighbridgeRow } from '../types';
import { ALL_COLUMNS } from '../components/columnsConfig';

export interface WeighbridgeApiResponse {
    status: string;
    date_from: string;
    date_to: string;
    page: number;
    per_page: number;
    total_records: number;
    total_pages: number;
    data: WeighbridgeRow[];
}

export const weighbridgeService = {
    async getReport(filters: WeighbridgeFilters, isExport: boolean = false): Promise<WeighbridgeApiResponse> {
        const params = new URLSearchParams();
        params.append('date_from', filters.date_from);
        params.append('date_to', filters.date_to);

        if (filters.company_id) {
            params.append('company_id', filters.company_id.toString());
        }

        if (filters.estate_ids && filters.estate_ids.length > 0) {
            filters.estate_ids.forEach((id) => {
                params.append('estate_ids', id.toString());
            });
        }

        if (isExport) {
            params.append('is_export', 'true');
        } else {
            params.append('page', (filters.page || 1).toString());
            params.append('per_page', (filters.per_page || 20).toString());
            if (filters.known_total_records !== undefined && filters.known_total_records > 0) {
                params.append('known_total_records', filters.known_total_records.toString());
            }
        }

        const response = await api.get<WeighbridgeApiResponse>(
            `/report/pijar/weighbridge?${params.toString()}`
        );
        return response.data;
    },

    async downloadFullExcel(
        filters: WeighbridgeFilters,
        filenamePrefix: string = 'Laporan_Timbang_Panen',
        visibleColumns?: string[]
    ) {
        const fullResponse = await this.getReport(filters, true);
        const data = fullResponse.data || [];
        if (data.length === 0) return;
        this.exportToExcel(data, filenamePrefix, visibleColumns);
    },

    exportToExcel(
        data: WeighbridgeRow[],
        filenamePrefix: string = 'Laporan_Timbang_Panen',
        visibleColumns?: string[]
    ) {
        if (!data || data.length === 0) return;

        // Filter kolom yang akan di-export sesuai kolom yang sedang aktif di table view
        const exportCols = visibleColumns && visibleColumns.length > 0
            ? ALL_COLUMNS.filter((col) => visibleColumns.includes(col.key as string))
            : ALL_COLUMNS;

        // Export columns using the EXACT same labels as in Table View
        const excelRows = data.map((row) => {
            const mappedRow: Record<string, any> = {};

            exportCols.forEach((col) => {
                const val = row[col.key];

                if (val === null || val === undefined) {
                    mappedRow[col.label] = col.isNumeric ? 0 : '';
                } else if (typeof val === 'boolean') {
                    mappedRow[col.label] = val ? 'Ya' : 'Tidak';
                } else if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
                    mappedRow[col.label] = val.split('T')[0];
                } else {
                    mappedRow[col.label] = val;
                }
            });

            return mappedRow;
        });

        const worksheet = XLSX.utils.json_to_sheet(excelRows);

        // Auto-fit lebar kolom (column width) agar rapi dan tidak terpotong (###)
        if (excelRows.length > 0) {
            const keys = Object.keys(excelRows[0]);
            const colWidths = keys.map((key) => {
                let maxLen = key.length;
                // Ambil sampel hingga 50 baris untuk efisiensi kalkulasi lebar
                const sampleRows = excelRows.slice(0, 50);
                sampleRows.forEach((row: any) => {
                    const val = row[key];
                    const valStr = val !== null && val !== undefined ? String(val) : '';
                    if (valStr.length > maxLen) {
                        maxLen = valStr.length;
                    }
                });
                return { wch: Math.min(Math.max(maxLen + 3, 10), 40) }; // min 10, max 40
            });
            worksheet['!cols'] = colWidths;
        }

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Laporan Timbang');

        const nowStr = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `${filenamePrefix}_${nowStr}.xlsx`);
    },
};
